import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Truck, Loader2, AlertCircle, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';
import { hospitalService } from '../services';
import Pagination from '../components/ui/Pagination';

const STATUS_STYLES = {
  ASSIGNED: 'bg-blue-100 text-blue-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  PENDING: 'bg-amber-100 text-amber-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    dateStyle: 'short',
    timeStyle: 'short',
    hour12: true
  });
};

const formatFare = (fare) => {
  if (fare == null || fare === '') return '—';
  return `₹${Number(fare).toLocaleString('en-IN')}`;
};

const VehicleHistoryPage = ({ vehicleId, plateNumber = '', onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 10;

  const fetchHistory = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await hospitalService.getVehicleHistory(vehicleId, { page, limit });
      setData(result?.data ?? result);
    } catch (err) {
      setError(err?.message || 'Failed to load vehicle history');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, page, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const vehicle = data?.vehicle;
  const summary = data?.summary;
  const history = data?.history ?? [];
  const pagination = data?.pagination ?? {};
  const totalPages = pagination.totalPages ?? 1;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Back + Title */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700"
          aria-label="Back to vehicles"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Truck className="w-6 h-6 text-gray-600" />
          <h1 className="text-xl font-semibold text-gray-900">
            Vehicle history {plateNumber ? `– ${plateNumber}` : ''}
          </h1>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Vehicle & fleet info */}
          {vehicle && (
            <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <p className="text-sm text-gray-500">Plate</p>
                  <p className="font-semibold text-gray-900">{vehicle.plate_number || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium text-gray-800">{vehicle.status || '—'}</p>
                </div>
                {vehicle.fleet_operator?.name && (
                  <div>
                    <p className="text-sm text-gray-500">Fleet operator</p>
                    <p className="font-medium text-gray-800">{vehicle.fleet_operator.name}</p>
                  </div>
                )}
                {vehicle.assigned_driver?.name && (
                  <div>
                    <p className="text-sm text-gray-500">Driver</p>
                    <p className="font-medium text-gray-800">{vehicle.assigned_driver.name}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                <FileText className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Total bookings</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.total_bookings ?? 0}</p>
                </div>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.completed_bookings ?? 0}</p>
                </div>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-600" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.active_bookings ?? 0}</p>
                </div>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm flex items-center gap-3">
                <XCircle className="w-8 h-8 text-red-600" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Cancelled</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.cancelled_bookings ?? 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* History table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Booking history</h2>
            </div>
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No bookings in history.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Booking ID</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Patient</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Pickup</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Dropoff</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Fare</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((row) => (
                        <tr key={row.booking_id} className="border-b border-gray-100 hover:bg-gray-50/50">
                          <td className="py-3 px-4 font-mono text-gray-900">{row.booking_id}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[row.status] ?? 'bg-gray-100 text-gray-800'}`}>
                              {row.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-700">{row.booking_type || '—'}</td>
                          <td className="py-3 px-4 text-gray-800">{row.patient_name || '—'}</td>
                          <td className="py-3 px-4 text-gray-600 max-w-[180px] truncate" title={row.pickup_address}>{row.pickup_address || '—'}</td>
                          <td className="py-3 px-4 text-gray-600 max-w-[180px] truncate" title={row.dropoff_address}>{row.dropoff_address || '—'}</td>
                          <td className="py-3 px-4 text-right text-gray-800">{formatFare(row.fare)}</td>
                          <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages} · {pagination.total ?? history.length} total
                    </span>
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleHistoryPage;
